/**
 * zone-api.ts — the only DOM interface the Composer agent ever touches.
 *
 * Spec: Zeq Orb v1 §5.2.
 *
 * The agent never calls document.querySelector, never sets innerHTML, never
 * appendChild's anything directly. Every change to the page goes through
 * one of these methods. Each one:
 *
 *   1. Resolves the target zone from its declared id.
 *   2. Verifies the zone is open ([data-zeq-zone] AND not under any
 *      [data-zeq-locked] ancestor).
 *   3. Verifies the operation is permitted by data-zeq-permissions.
 *   4. Sanitises the payload (sanitiser.ts).
 *   5. Applies the change inside the zone's containment (containment.css).
 *   6. On any failure, logs to the audit chain via rejection-log.ts and
 *      returns a structured rejection — never throws.
 *
 * The Pulse cannot break the page because the page is structurally
 * partitioned at build time. Locked elements have no API surface. Zones
 * have a tiny one. The agent learns the names of the zones and the names
 * of these methods, and that's all.
 */

import { sanitise, isSanitiseTrivial, type SanitiseResult } from "./sanitiser.js";
import { logRejection } from "./rejection-log.js";

// ─── Permission types ───────────────────────────────────────────────────

/** What an operation is asking for. Compared against the zone's permissions. */
type OperationKind = "content" | "style" | "arrange" | "components";

/** Whitespace-separated permission tokens declared in data-zeq-permissions. */
function getZonePermissions(zone: HTMLElement): Set<OperationKind> {
  const raw = (zone.getAttribute("data-zeq-permissions") || "")
    .toLowerCase()
    .split(/[\s,]+/)
    .filter(Boolean) as OperationKind[];
  return new Set(raw);
}

// ─── Zone resolution ─────────────────────────────────────────────────────

/**
 * Resolve a zoneId to its element. Strict — refuses to follow into a
 * locked subtree even if the zone would otherwise be present.
 *
 * A zone is identified by either:
 *   - its data-zeq-zone attribute value (preferred); or
 *   - its DOM id.
 *
 * If both match, data-zeq-zone wins (more specific).
 */
function resolveZone(zoneId: string): HTMLElement | null {
  if (!zoneId || typeof zoneId !== "string") return null;
  // Data-attr match first.
  const byAttr = document.querySelector(
    `[data-zeq-zone="${cssEscape(zoneId)}"]`,
  ) as HTMLElement | null;
  const byId = document.getElementById(zoneId);
  const candidate = byAttr ?? byId;
  if (!candidate) return null;
  // Reject anything that lives under a locked ancestor.
  if (isUnderLocked(candidate)) return null;
  // The element itself must be a zone.
  if (!candidate.hasAttribute("data-zeq-zone")) return null;
  return candidate;
}

function isUnderLocked(el: HTMLElement): boolean {
  let cur: HTMLElement | null = el;
  while (cur) {
    if (cur.hasAttribute("data-zeq-locked")) return true;
    cur = cur.parentElement;
  }
  return false;
}

// CSS.escape polyfill for older browsers — small inline copy so we don't
// depend on CSS.escape being present.
function cssEscape(s: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") return CSS.escape(s);
  return s.replace(/([!"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, "\\$1");
}

// ─── Result envelope ────────────────────────────────────────────────────

/** Every Zone API method returns this shape. Never throws. */
export interface ZoneResult<T = unknown> {
  success: boolean;
  zoneId: string;
  /** Local Zeqond at completion. */
  zeqond: number;
  /** Detail data for the caller (varies per method). */
  data?: T;
  /** Reason the operation was rejected, if !success. */
  reason?: string;
  /** Sanitiser report, if the call passed through sanitise(). */
  stripped?: SanitiseResult;
}

function nowZeqond(): number {
  return Math.floor(Date.now() / 1000 / 0.777);
}

// ─── ZeqZone.write ──────────────────────────────────────────────────────

export function write(zoneId: string, content: string): ZoneResult<{ html: string }> {
  const zone = resolveZone(zoneId);
  if (!zone) {
    logRejection({
      reason: "target_is_not_a_zone",
      intent: "ZeqZone.write",
      zoneId,
      target: zoneId,
    });
    return { success: false, zoneId, zeqond: nowZeqond(), reason: "target_is_not_a_zone" };
  }
  const perms = getZonePermissions(zone);
  if (!perms.has("content") && !perms.has("components")) {
    logRejection({
      reason: "permission_denied",
      intent: "ZeqZone.write",
      zoneId,
    });
    return { success: false, zoneId, zeqond: nowZeqond(), reason: "permission_denied" };
  }

  // Empty / whitespace input clears the zone — explicit and harmless.
  if (typeof content !== "string") content = "";
  const trimmed = content.trim();

  let cleaned: SanitiseResult;
  if (isSanitiseTrivial(trimmed)) {
    // Pure text — no HTML to clean. Wrap with textContent for safety.
    zone.textContent = trimmed;
    cleaned = { html: trimmed, tagsHardDropped: [], tagsUnwrapped: [], attrsStripped: [] };
  } else {
    cleaned = sanitise(content);
    zone.innerHTML = cleaned.html;
  }

  // If anything was stripped, log a content_blocked entry for transparency
  // (the call still succeeded; the agent's intent is just visibly redacted).
  if (cleaned.tagsHardDropped.length > 0 || cleaned.attrsStripped.length > 0) {
    logRejection({
      reason: "content_blocked",
      intent: "ZeqZone.write — sanitiser stripped content",
      zoneId,
      stripped: {
        tags: [...cleaned.tagsHardDropped, ...cleaned.tagsUnwrapped],
        attrs: cleaned.attrsStripped,
      },
    });
  }

  return {
    success: true,
    zoneId,
    zeqond: nowZeqond(),
    data: { html: cleaned.html },
    stripped: cleaned,
  };
}

// ─── ZeqZone.style ──────────────────────────────────────────────────────

/** Properties the agent is allowed to set as inline styles. */
const ALLOWED_STYLE_PROPS = new Set<string>([
  // Colour + typography
  "color", "background", "background-color", "background-image",
  "font-family", "font-size", "font-weight", "font-style", "font-variant",
  "line-height", "letter-spacing", "text-align", "text-decoration",
  "text-transform", "text-shadow",
  // Spacing
  "padding", "padding-top", "padding-right", "padding-bottom", "padding-left",
  "margin", "margin-top", "margin-right", "margin-bottom", "margin-left",
  "gap", "row-gap", "column-gap",
  // Sizing — bounded to the zone box, safe.
  "width", "height", "min-width", "min-height", "max-width", "max-height",
  "aspect-ratio",
  // Layout (within zone)
  "display", "flex-direction", "flex-wrap", "justify-content", "align-items",
  "align-content", "align-self", "justify-self", "flex-grow", "flex-shrink",
  "flex-basis", "flex", "order",
  "grid-template-columns", "grid-template-rows", "grid-column", "grid-row",
  "grid-gap", "grid-auto-flow", "place-items", "place-content",
  // Borders + shape
  "border", "border-top", "border-right", "border-bottom", "border-left",
  "border-color", "border-style", "border-width", "border-radius",
  "outline", "outline-color", "outline-style", "outline-width", "outline-offset",
  // Effects (no compositing escape — they're contained by paint containment)
  "box-shadow", "filter", "backdrop-filter", "opacity",
  "transform", "transform-origin", "transition", "animation",
  // Theme tokens — agent-defined CSS custom properties
  // (caught by the --prefix check below)
]);

const BLOCKED_STYLE_PROPS = new Set<string>([
  "position",  // sanitiser already strips fixed/absolute; we strip the property entirely
  "z-index",
  "inset", "top", "right", "bottom", "left",
  "contain",   // can't override our own containment
  "isolation",
  "overflow", "overflow-x", "overflow-y", "clip-path",
  "pointer-events",
  // Layer escape vectors
  "mix-blend-mode", "background-attachment",
  // Page-level attachments
  "page", "page-break-after", "page-break-before", "page-break-inside",
]);

export function style(zoneId: string, styles: Record<string, string>): ZoneResult<{ applied: Record<string, string> }> {
  const zone = resolveZone(zoneId);
  if (!zone) {
    logRejection({ reason: "target_is_not_a_zone", intent: "ZeqZone.style", zoneId, target: zoneId });
    return { success: false, zoneId, zeqond: nowZeqond(), reason: "target_is_not_a_zone" };
  }
  const perms = getZonePermissions(zone);
  if (!perms.has("style") && !perms.has("components")) {
    logRejection({ reason: "permission_denied", intent: "ZeqZone.style", zoneId });
    return { success: false, zoneId, zeqond: nowZeqond(), reason: "permission_denied" };
  }

  const applied: Record<string, string> = {};
  const blocked: string[] = [];

  for (const [propRaw, valRaw] of Object.entries(styles || {})) {
    const prop = String(propRaw).trim().toLowerCase();
    const val = String(valRaw).trim();

    // Custom properties (theme tokens) — always allowed.
    if (prop.startsWith("--")) {
      if (looksDangerous(val)) { blocked.push(`${prop}=<dangerous-value>`); continue; }
      zone.style.setProperty(prop, val);
      applied[prop] = val;
      continue;
    }

    if (BLOCKED_STYLE_PROPS.has(prop)) { blocked.push(prop); continue; }
    if (!ALLOWED_STYLE_PROPS.has(prop)) { blocked.push(prop); continue; }
    if (looksDangerous(val)) { blocked.push(`${prop}=<dangerous-value>`); continue; }

    zone.style.setProperty(prop, val);
    applied[prop] = val;
  }

  if (blocked.length > 0) {
    logRejection({
      reason: "containment_escape",
      intent: "ZeqZone.style — properties or values rejected",
      zoneId,
      stripped: { attrs: blocked },
    });
  }

  return { success: true, zoneId, zeqond: nowZeqond(), data: { applied } };
}

function looksDangerous(val: string): boolean {
  const lower = val.toLowerCase();
  return (
    lower.includes("expression(") ||
    lower.includes("javascript:") ||
    lower.includes("vbscript:") ||
    lower.includes("@import") ||
    lower.includes("position:fixed") ||
    lower.includes("position: fixed") ||
    lower.includes("position:absolute") ||
    lower.includes("position: absolute")
  );
}

// ─── ZeqZone.arrange ────────────────────────────────────────────────────

export interface LayoutInstruction {
  type: "flex" | "grid";
  direction?: "row" | "column" | "row-reverse" | "column-reverse";
  gap?: string;
  /**
   * Reorder children by their data-zeq-id values.
   * Children without an id stay in current position relative to each other.
   */
  childrenOrder?: string[];
}

export function arrange(zoneId: string, layout: LayoutInstruction): ZoneResult<{ layout: LayoutInstruction }> {
  const zone = resolveZone(zoneId);
  if (!zone) {
    logRejection({ reason: "target_is_not_a_zone", intent: "ZeqZone.arrange", zoneId, target: zoneId });
    return { success: false, zoneId, zeqond: nowZeqond(), reason: "target_is_not_a_zone" };
  }
  const perms = getZonePermissions(zone);
  if (!perms.has("arrange") && !perms.has("components")) {
    logRejection({ reason: "permission_denied", intent: "ZeqZone.arrange", zoneId });
    return { success: false, zoneId, zeqond: nowZeqond(), reason: "permission_denied" };
  }

  if (layout.type === "flex") {
    zone.style.display = "flex";
    if (layout.direction) zone.style.flexDirection = layout.direction;
  } else if (layout.type === "grid") {
    zone.style.display = "grid";
  }
  if (layout.gap) zone.style.gap = sanitiseSpacingValue(layout.gap);

  if (Array.isArray(layout.childrenOrder)) {
    reorderChildren(zone, layout.childrenOrder);
  }

  return { success: true, zoneId, zeqond: nowZeqond(), data: { layout } };
}

function sanitiseSpacingValue(v: string): string {
  // Allow only number+unit (e.g. "8px", "1rem", "2em", "0.5ch", "8px 16px").
  if (/^[\d.\s]+(?:px|rem|em|ch|%|vw|vh|fr)?(?:\s+[\d.\s]+(?:px|rem|em|ch|%|vw|vh|fr)?)?$/i.test(v)) return v;
  return "8px"; // safe fallback
}

function reorderChildren(zone: HTMLElement, order: string[]): void {
  const map = new Map<string, HTMLElement>();
  Array.from(zone.children).forEach((c) => {
    const el = c as HTMLElement;
    const id = el.getAttribute("data-zeq-id");
    if (id) map.set(id, el);
  });
  for (const id of order) {
    const el = map.get(id);
    if (el) zone.appendChild(el); // appendChild on already-present element re-positions
  }
}

// ─── ZeqZone.component ──────────────────────────────────────────────────

/**
 * Component definition. The component registry is the agent's only way
 * to mount complex Pulse UI (live-readout, charts, proof-trail, etc.).
 *
 * Week 1 ships the schema; component implementations land Week 2.
 */
export interface ComponentDef {
  type:
    | "live-readout"
    | "chart"
    | "proof-trail"
    | "audit-clock"
    | "state-explorer"
    | "linktree"
    | "inbox"
    | "data-table";
  props: Record<string, unknown>;
  contractBinding?: { contractId: string; inputs: Record<string, unknown> };
}

const KNOWN_COMPONENT_TYPES = new Set<ComponentDef["type"]>([
  "live-readout", "chart", "proof-trail", "audit-clock",
  "state-explorer", "linktree", "inbox", "data-table",
]);

export function component(zoneId: string, def: ComponentDef): ZoneResult<{ componentId: string }> {
  const zone = resolveZone(zoneId);
  if (!zone) {
    logRejection({ reason: "target_is_not_a_zone", intent: "ZeqZone.component", zoneId, target: zoneId });
    return { success: false, zoneId, zeqond: nowZeqond(), reason: "target_is_not_a_zone" };
  }
  const perms = getZonePermissions(zone);
  if (!perms.has("components")) {
    logRejection({ reason: "permission_denied", intent: "ZeqZone.component", zoneId });
    return { success: false, zoneId, zeqond: nowZeqond(), reason: "permission_denied" };
  }
  if (!def || !KNOWN_COMPONENT_TYPES.has(def.type)) {
    logRejection({
      reason: "unknown_component",
      intent: `ZeqZone.component — type=${def?.type}`,
      zoneId,
    });
    return { success: false, zoneId, zeqond: nowZeqond(), reason: "unknown_component" };
  }

  // Week 1 ships an inert host element (`<zeq-{type}>`) so the layout is
  // visible. Hydration of components is a Week 2 task — that's where the
  // ZeqondClock + ContractClient connect to actual operator outputs.
  const componentId = `zeq-${def.type}-${Math.random().toString(36).slice(2, 10)}`;
  const host = document.createElement(`zeq-${def.type}`);
  host.setAttribute("data-zeq-component-id", componentId);
  host.setAttribute("data-zeq-component-type", def.type);
  host.setAttribute("data-zeq-props", JSON.stringify(def.props || {}));
  if (def.contractBinding) {
    host.setAttribute("data-zeq-contract-id", def.contractBinding.contractId);
    host.setAttribute("data-zeq-contract-inputs", JSON.stringify(def.contractBinding.inputs || {}));
  }
  zone.appendChild(host);

  return { success: true, zoneId, zeqond: nowZeqond(), data: { componentId } };
}

// ─── ZeqZone.read ───────────────────────────────────────────────────────

/** Reads are always permitted — agents can see the page. */
export function read(zoneId: string): ZoneResult<{ html: string; text: string; components: string[] }> {
  const zone = resolveZone(zoneId);
  if (!zone) {
    return { success: false, zoneId, zeqond: nowZeqond(), reason: "target_is_not_a_zone" };
  }
  const components = Array.from(zone.querySelectorAll("[data-zeq-component-type]"))
    .map((c) => c.getAttribute("data-zeq-component-type") || "")
    .filter(Boolean);
  return {
    success: true,
    zoneId,
    zeqond: nowZeqond(),
    data: {
      html: zone.innerHTML,
      text: zone.textContent || "",
      components,
    },
  };
}

// ─── ZeqZone.clear ──────────────────────────────────────────────────────

export function clear(zoneId: string): ZoneResult<null> {
  const zone = resolveZone(zoneId);
  if (!zone) {
    logRejection({ reason: "target_is_not_a_zone", intent: "ZeqZone.clear", zoneId, target: zoneId });
    return { success: false, zoneId, zeqond: nowZeqond(), reason: "target_is_not_a_zone" };
  }
  const perms = getZonePermissions(zone);
  if (!perms.has("content") && !perms.has("components")) {
    logRejection({ reason: "permission_denied", intent: "ZeqZone.clear", zoneId });
    return { success: false, zoneId, zeqond: nowZeqond(), reason: "permission_denied" };
  }
  zone.replaceChildren();
  return { success: true, zoneId, zeqond: nowZeqond(), data: null };
}

// ─── Public surface ─────────────────────────────────────────────────────

/**
 * The single `ZeqZone` namespace the Composer agent talks to.
 *
 * Usage from the Pulse runtime (Week 2+):
 *   import { ZeqZone } from "@zeq/pulse";
 *   await ZeqZone.write("hero", "<h1>Welcome</h1>");
 */
export const ZeqZone = {
  write,
  style,
  arrange,
  component,
  read,
  clear,
};

export default ZeqZone;
